<template>
  <base-draggable-modal
      name="replace_article_modal"
      max-height="80vh"
      width="70vw"
      :fullscreen="$vuetify.display.xsOnly"
      ref="replace_article_modal">
    <v-card rounded="xl">
      <base-modal>
        <template #header>
          <v-toolbar class="draggable_selector" flat color="secondary" density="compact">
            <v-toolbar-title class="text-primary">{{ $store.getters.translate('articles') }}</v-toolbar-title>
          </v-toolbar>
        </template>
        <template #content>
          <v-card-text>
            <template v-if="articles.length > 0">
              <div class="lg-medium text-primary">{{ $store.getters.translate("old") + " " + $store.getters.translate("article") }}</div>
              <v-autocomplete
                  class="reg-normal text-primary mt-3"
                  base-color="border-tertiary"
                  bg-color="secondary"
                  density="default"
                  v-model="old_article_id"
                  :items="articles"
                  item-value="id"
                  item-title="name"
                  :return-object="false"
                  hide-details
                  clearable>
                <template #label>
                  <span class="sm-normal text-subtle">{{ $store.getters.translate('article') }}</span>
                </template>
              </v-autocomplete>
              <div class="sm-medium mt-3">{{ $store.getters.translate("move_old_article_to") }}</div>
              <v-autocomplete
                  class="reg-normal text-primary mt-3"
                  base-color="border-tertiary"
                  bg-color="secondary"
                  density="default"
                  v-model="new_root_asset_id"
                  :items="root_assets"
                  item-value="id"
                  item-title="name"
                  :return-object="false"
                  hide-details
                  clearable>
                <template #label>
                  <span class="sm-normal text-subtle">{{ $store.getters.translate('root_asset_id') }}</span>
                </template>
              </v-autocomplete>
              <v-autocomplete
                  v-if="new_root_asset_id"
                  class="reg-normal text-primary mt-3"
                  base-color="border-tertiary"
                  bg-color="secondary"
                  density="default"
                  v-model="new_tag_number_id"
                  :items="tag_numbers.filter(item => item.filter === new_root_asset_id)"
                  :return-object="false"
                  item-value="value"
                  item-title="text"
                  hide-details
                  clearable>
                <template #label>
                  <span class="sm-normal text-subtle">{{ $store.getters.translate('tag_number_id') }}<strong>*</strong></span>
                </template>
              </v-autocomplete>
              <div class="sm-medium mt-3">{{ $store.getters.translate("change_equipment_reason") }}</div>
              <v-textarea
                  density="default"
                  class="reg-normal text-primary mt-3"
                  base-color="border-tertiary"
                  bg-color="secondary"
                  color="primary"
                  v-model="change_reason"
                  hide-details
                  flat
                  clearable>
                <template #label>
                  <span class="sm-normal text-subtle">{{ $store.getters.translate('reason') }}<strong>*</strong></span>
                </template>
              </v-textarea>
              <div class="lg-medium text-primary mt-3">{{ $store.getters.translate("new") + " " + $store.getters.translate("article") }}</div>
            </template>
            <v-toolbar flat color="white" density="compact" class="mt-3">
              <v-text-field
                  density="compact"
                  class="reg-normal text-primary"
                  base-color="border-tertiary"
                  bg-color="secondary"
                  color="primary"
                  v-model="search"
                  hide-details
                  single-line
                  flat
                  clearable>
                <template #label>
                  <span class="sm-normal text-subtle">{{ $store.getters.translate('search') }}</span>
                </template>
              </v-text-field>
              <v-autocomplete
                  class="reg-normal text-primary ml-5"
                  base-color="border-tertiary"
                  bg-color="secondary"
                  density="compact"
                  v-model="advanced_search.root_asset_name"
                  :items="advanced_search_models.root_asset_name"
                  @update:model-value="showRecords"
                  :return-object="false"
                  hide-details
                  single-line
                  clearable>
                <template #label>
                  <span class="sm-normal text-subtle">{{ $store.getters.translate('root_asset_name') }}</span>
                </template>
              </v-autocomplete>
              <v-autocomplete
                  class="reg-normal text-primary ml-5"
                  base-color="border-tertiary"
                  bg-color="secondary"
                  density="compact"
                  v-model="advanced_search.equipment_type_name"
                  :items="advanced_search_models.equipment_type_name"
                  @update:model-value="showRecords"
                  :return-object="false"
                  item-value="value"
                  item-title="text"
                  hide-details
                  single-line
                  clearable>
                <template #label>
                  <span class="sm-normal text-subtle">{{ $store.getters.translate('equipment_type') }}</span>
                </template>
              </v-autocomplete>
              <v-autocomplete
                  class="reg-normal text-primary ml-5"
                  base-color="border-tertiary"
                  bg-color="secondary"
                  density="compact"
                  v-model="advanced_search.brand_name"
                  :items="advanced_search_models.brand_name"
                  @update:model-value="showRecords"
                  hide-details
                  single-line
                  clearable>
                <template #label>
                  <span class="sm-normal text-subtle">{{ $store.getters.translate('brand') }}</span>
                </template>
              </v-autocomplete>
            </v-toolbar>
            <div style="overflow-x: auto; width: 100%;">
              <v-data-table
                  :headers="headers"
                  :items="getRecords"
                  class="elevation-1"
                  item-value="id"
                  selectable-key="id"
                  v-model="selected"
                  :search="search"
                  v-bind="footer_props"
                  v-model:items-per-page="options.itemsPerPage"
                  v-model:page="options.page"
                  show-select>
                <template #header.data-table-select/>
                <template #item.equipment_type_name="{ item }">
                  {{ $store.getters.translate(item.equipment_type_name) }}
                </template>
                <template #no-data>
                  <span class="sm-normal link">{{ $store.getters.translate("nothing_found") }}</span>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </template>
        <template #footer>
          <v-card-text>
            <v-row>
              <v-col>
                <base-action-button :width="'100%'" @action="reset">
                  <template #prepend><PhX style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
                  {{ $store.getters.translate("cancel") }}
                </base-action-button>
              </v-col>
              <v-col>
                <base-action-button :background="true" :width="'100%'" @action="save">
                  <template #prepend><PhFloppyDisk style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
                  {{ $store.getters.translate("save") }}
                </base-action-button>
              </v-col>
            </v-row>
          </v-card-text>
        </template>
      </base-modal>
    </v-card>
  </base-draggable-modal>
</template>

<script>
import helpFunctions from "../../plugins/helpFunctions";
import BaseModal from "./BaseModal";
import BaseDraggableModal from "./BaseDraggableModal";
import BaseActionButton from "./BaseActionButton";
import {PhFloppyDisk, PhX} from "@phosphor-icons/vue";

export default {
  props: ['tag_number_id', 'articles'],
  components: { BaseActionButton, BaseDraggableModal, BaseModal, PhFloppyDisk, PhX },
  data() {
    return {
      headers: [],
      all_records: [],
      records: [],
      root_assets: [],
      tag_numbers: [],
      old_article_id: null,
      new_root_asset_id: null,
      new_tag_number_id: null,
      change_reason: null,
      selected: [],
      options: helpFunctions.table_options,
      footer_props: helpFunctions.footer_props,
      search: '',
      advanced_search: {
        root_asset_name: null,
        equipment_type_name: null,
        brand_name: null,
      },
      advanced_search_models: {
        root_asset_name: [],
        equipment_type_name: [],
        brand_name: [],
      },
    };
  },
  created() {
    this.headers = helpFunctions.composeTableHeaders(helpFunctions.headers.articles.filter(item => item !== 'actions'));
  },
  methods: {
    ...helpFunctions.modal_functions,
    async load() {
      this.openModal('replace_article_modal');
      this.$store.dispatch('setLoader', true);
      await this.getRootAssets();
      await this.loadTagNumbers();
      this.all_records = await helpFunctions.getCustomRoute(this.$toast, 'articles-full-info', false);
      this.$store.dispatch('setLoader', false);
      this.fillAdvancedSearchArrays();
    },
    async getRootAssets() {
      //CAN`T USE helpFunctions.getRootAssets BECAUSE IT LOADS ALL ASSETS VIA ADDITIONAL MODELS CONTROLLER WITHOUT CHECKING PERMISSIONS
      //this.root_assets = await helpFunctions.getRootAssets(this.$toast);
      this.root_assets = await helpFunctions.getCustomRoute(this.$toast, "assets?root=true", false);
    },
    async loadTagNumbers() {
      this.tag_numbers = await helpFunctions.getAdditionalModelsForDropdowns("tagnumber", "id", ["name"], " | ", "root_asset_id");
    },
    fillAdvancedSearchArrays() {
      let processed_equipment_types = [];
      this.all_records.forEach(record => {
        if(record.root_asset_name && !this.advanced_search_models.root_asset_name.includes(record.root_asset_name)) {
          this.advanced_search_models.root_asset_name.push(record.root_asset_name);
        }
        if(record.equipment_type_name && !processed_equipment_types.includes(record.equipment_type_name)) {
          processed_equipment_types.push(record.equipment_type_name);
          this.advanced_search_models.equipment_type_name.push({ value: record.equipment_type_name, text: this.$store.getters.translate(record.equipment_type_name) });
        }
        if(record.brand_name && !this.advanced_search_models.brand_name.includes(record.brand_name)) {
          this.advanced_search_models.brand_name.push(record.brand_name);
        }
      });
      this.showRecords();
    },
    showRecords() {
      let sorted_array = this.all_records;
      if (this.advanced_search.root_asset_name) {
        sorted_array = sorted_array.filter(item => item.root_asset_name === this.advanced_search.root_asset_name);
      }
      if (this.advanced_search.equipment_type_name) {
        sorted_array = sorted_array.filter(item => item.equipment_type_name === this.advanced_search.equipment_type_name);
      }
      if (this.advanced_search.brand_name) {
        sorted_array = sorted_array.filter(item => item.brand_name === this.advanced_search.brand_name);
      }
      this.records = sorted_array;
    },
    async save() {
      if (this.selected.length === 0) {
        this.$toast.error(this.$store.getters.translate("please_select_article"));
      }
      else {
        let error = false;
        if(this.old_article_id) {
          if(!this.new_root_asset_id || !this.new_tag_number_id) {
            this.$toast.error(this.$store.getters.translate("required_field") + ": " + this.$store.getters.translate('root_asset_id') + ", " + this.$store.getters.translate('tag_number_id'));
            error = true;
          }
          else if(!this.change_reason || this.change_reason === '') {
            this.$toast.error(this.$store.getters.translate("change_reason_required"));
            error = true;
          }
        }
        if(!error) {
          //move old article first
          if(this.old_article_id) {
            await helpFunctions.postCustomRoute(this.$toast, "move-article", { article_id: this.old_article_id, tag_number_id: this.new_tag_number_id, change_reason: this.change_reason }, false, true);
          }
          //move new article
          if (await helpFunctions.postCustomRoute(this.$toast, "move-article", { article_id: this.selected[0], tag_number_id: this.tag_number_id }, true, true)) {
            this.$emit('refresh');
            this.reset();
          }
        }
      }
    },
    reset() {
      this.closeModal('replace_article_modal');
      this.all_records = [];
      this.records = [];
      this.old_article_id = null;
      this.new_root_asset_id = null;
      this.new_tag_number_id = null;
      this.change_reason = null;
      this.selected = [];
      this.search = '';
      this.advanced_search = {
        root_asset_name: null,
        equipment_type_name: null,
        brand_name: null,
      };
      this.advanced_search_models = {
        root_asset_name: [],
        equipment_type_name: [],
        brand_name: [],
      };
    },
  },
  computed: {
    getRecords() {
      if (this.records) {
        return this.records;
      } else {
        return [];
      }
    },
  },
  watch: {
    options: {
      handler() {
        this.showRecords();
      },
      deep: true,
    },
    advanced_search: {
      handler() {
        this.options.page = 1;
      },
      deep: true,
    },
    selected: {
      handler() {
        if(this.selected.length > 1) {
          this.$toast.error(this.$store.getters.translate("only_one_article_selection"));
          this.$nextTick(() => {
            this.selected.pop();
          });
        }
      },
    },
  },
};
</script>

<style scoped>
.v-checkbox {
  display: flex;
}
</style>